import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { 
  Tag, 
  Calculator, 
  ClipboardList, 
  Package, 
  Users, 
  Settings, 
  Wrench,
  FileText,
  QrCode,
  Calendar,
  Newspaper,
  Building2,
  CheckCircle2,
  BarChart3,
  Cog,
  Scissors,
  Grid3X3,
  FolderOpen,
  Layers,
  HardHat,
  Hammer,
  Timer,
  Route,
  Database,
  Zap,
  Plus,
  X,
  GripVertical,
  Settings2,
  LucideIcon
} from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import { ScrollArea } from "@/components/ui/scroll-area";
import { cn } from "@/lib/utils";

// Todos los módulos disponibles
const ALL_MODULES: { id: string; name: string; href: string; icon: LucideIcon; category: string }[] = [
  // Destacados
  { id: "labels", name: "Etiquetas", href: "/labels", icon: Tag, category: "Producción" },
  { id: "quotations", name: "Cotizaciones", href: "/quotations", icon: Calculator, category: "Gestión" },
  
  // Dashboard
  { id: "dashboard", name: "Dashboard", href: "/", icon: BarChart3, category: "Dashboard" },
  { id: "executive", name: "Dashboard Ejecutivo", href: "/executive", icon: BarChart3, category: "Dashboard" },
  
  // Operaciones
  { id: "operator", name: "Vista Operador", href: "/operator", icon: Users, category: "Operaciones" },
  { id: "personal", name: "Personal", href: "/personal", icon: HardHat, category: "Operaciones" },
  
  // Producción
  { id: "work-orders", name: "Órdenes de Trabajo", href: "/work-orders", icon: ClipboardList, category: "Producción" },
  { id: "processes", name: "Procesos", href: "/processes", icon: Settings2, category: "Producción" },
  { id: "molds", name: "Moldes", href: "/molds", icon: Wrench, category: "Producción" },
  { id: "machines", name: "Máquinas", href: "/machines", icon: Cog, category: "Producción" },
  { id: "recipes", name: "Recetas", href: "/recipes", icon: Timer, category: "Producción" },
  { id: "tools", name: "Herramientas", href: "/tools", icon: Hammer, category: "Producción" },
  
  // Proyectos
  { id: "projects", name: "Proyectos", href: "/projects", icon: FolderOpen, category: "Proyectos" },
  
  // Inventario
  { id: "material-inventory", name: "Materiales", href: "/materials", icon: Package, category: "Inventario" },
  { id: "plotter-cuts", name: "Corte Plotter", href: "/plotter-cuts", icon: Grid3X3, category: "Inventario" },
  { id: "cut-material", name: "Corte Taller", href: "/cut-material", icon: Scissors, category: "Inventario" },
  
  // Gestión
  { id: "customers", name: "Clientes", href: "/customers", icon: Building2, category: "Gestión" },
  { id: "events", name: "Eventos", href: "/events", icon: Calendar, category: "Gestión" },
  { id: "news", name: "Noticias", href: "/news", icon: Newspaper, category: "Gestión" },
  
  // Calidad
  { id: "quality-control", name: "Control de Calidad", href: "/quality-control", icon: CheckCircle2, category: "Calidad" },
  { id: "traceability", name: "Trazabilidad", href: "/traceability", icon: Route, category: "Calidad" },
  { id: "nfc-captures", name: "Capturas NFC", href: "/nfc-captures", icon: Database, category: "Calidad" },
  { id: "qr-traceability", name: "Trazabilidad QR", href: "/qr-traceability", icon: QrCode, category: "Calidad" },
  
  // Sistema
  { id: "reports", name: "Reportes", href: "/reports", icon: FileText, category: "Sistema" },
  { id: "analytics", name: "Analíticas", href: "/analytics", icon: BarChart3, category: "Sistema" },
  { id: "settings", name: "Configuración", href: "/settings", icon: Settings, category: "Sistema" },
];

// Accesos rápidos por defecto
const DEFAULT_QUICK_ACCESS = ["labels", "quotations", "work-orders", "customers"];

const STORAGE_KEY = "athenea_quick_access";

export function QuickAccessPanel() {
  const navigate = useNavigate();
  const [quickAccess, setQuickAccess] = useState<string[]>([]);
  const [isConfigOpen, setIsConfigOpen] = useState(false);

  // Cargar configuración de localStorage
  useEffect(() => {
    const saved = localStorage.getItem(STORAGE_KEY);
    if (saved) {
      try {
        setQuickAccess(JSON.parse(saved));
      } catch {
        setQuickAccess(DEFAULT_QUICK_ACCESS);
      }
    } else {
      setQuickAccess(DEFAULT_QUICK_ACCESS);
    }
  }, []);

  // Guardar en localStorage cuando cambie
  const saveQuickAccess = (newAccess: string[]) => {
    setQuickAccess(newAccess);
    localStorage.setItem(STORAGE_KEY, JSON.stringify(newAccess));
  };

  const addToQuickAccess = (moduleId: string) => {
    if (!quickAccess.includes(moduleId) && quickAccess.length < 8) {
      saveQuickAccess([...quickAccess, moduleId]);
    }
  };

  const removeFromQuickAccess = (moduleId: string) => {
    saveQuickAccess(quickAccess.filter(id => id !== moduleId));
  };

  const getModuleById = (id: string) => ALL_MODULES.find(m => m.id === id);

  // Agrupar módulos por categoría
  const modulesByCategory = ALL_MODULES.reduce((acc, module) => {
    if (!acc[module.category]) {
      acc[module.category] = [];
    }
    acc[module.category].push(module);
    return acc;
  }, {} as Record<string, typeof ALL_MODULES>);

  return (
    <Card className="border-primary/20 bg-gradient-to-r from-primary/5 to-accent/5">
      <CardHeader className="p-3 sm:p-4 pb-2 sm:pb-3">
        <div className="flex items-center justify-between">
          <CardTitle className="text-sm sm:text-lg flex items-center gap-1.5 sm:gap-2">
            <Zap className="w-4 h-4 sm:w-5 sm:h-5 text-primary" />
            Accesos Rápidos
          </CardTitle>
          <Dialog open={isConfigOpen} onOpenChange={setIsConfigOpen}>
            <DialogTrigger asChild>
              <Button variant="ghost" size="sm" className="h-7 w-7 sm:h-8 sm:w-8 p-0">
                <Settings2 className="w-3.5 h-3.5 sm:w-4 sm:h-4" />
              </Button>
            </DialogTrigger>
            <DialogContent className="max-w-[calc(100vw-2rem)] sm:max-w-md max-h-[80vh] mx-auto">
              <DialogHeader>
                <DialogTitle className="text-base sm:text-lg">Configurar Accesos Rápidos</DialogTitle>
              </DialogHeader>
              <ScrollArea className="h-[50vh] sm:h-[60vh] pr-4">
                <div className="space-y-4">
                  {/* Módulos actuales */}
                  <div>
                    <h4 className="text-xs sm:text-sm font-medium mb-2 text-muted-foreground">
                      Accesos actuales ({quickAccess.length}/8)
                    </h4>
                    <div className="flex flex-wrap gap-1.5 sm:gap-2">
                      {quickAccess.map(id => {
                        const module = getModuleById(id);
                        if (!module) return null;
                        const Icon = module.icon;
                        return (
                          <div
                            key={id}
                            className="flex items-center gap-1 px-1.5 sm:px-2 py-0.5 sm:py-1 bg-primary/10 rounded-md text-xs sm:text-sm"
                          >
                            <Icon className="w-3 h-3" />
                            <span className="max-w-[80px] sm:max-w-none truncate">{module.name}</span>
                            <button
                              onClick={() => removeFromQuickAccess(id)}
                              className="ml-0.5 sm:ml-1 hover:text-destructive"
                            >
                              <X className="w-3 h-3" />
                            </button>
                          </div>
                        );
                      })}
                    </div>
                  </div>

                  {/* Todos los módulos por categoría */}
                  {Object.entries(modulesByCategory).map(([category, modules]) => (
                    <div key={category}>
                      <h4 className="text-xs sm:text-sm font-medium mb-2">{category}</h4>
                      <div className="grid grid-cols-2 gap-1.5 sm:gap-2">
                        {modules.map(module => {
                          const Icon = module.icon;
                          const isAdded = quickAccess.includes(module.id);
                          return (
                            <button
                              key={module.id}
                              onClick={() => isAdded ? removeFromQuickAccess(module.id) : addToQuickAccess(module.id)}
                              disabled={!isAdded && quickAccess.length >= 8}
                              className={cn(
                                "flex items-center gap-1.5 sm:gap-2 p-1.5 sm:p-2 rounded-md text-xs sm:text-sm text-left transition-colors",
                                isAdded 
                                  ? "bg-primary text-primary-foreground" 
                                  : "bg-muted hover:bg-muted/80",
                                !isAdded && quickAccess.length >= 8 && "opacity-50 cursor-not-allowed"
                              )}
                            >
                              <Icon className="w-3.5 h-3.5 sm:w-4 sm:h-4 flex-shrink-0" />
                              <span className="truncate">{module.name}</span>
                            </button>
                          );
                        })}
                      </div>
                    </div>
                  ))}
                </div>
              </ScrollArea>
            </DialogContent>
          </Dialog>
        </div>
      </CardHeader>
      <CardContent className="p-3 sm:p-4 pt-0">
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-1.5 sm:gap-2">
          {quickAccess.map(id => {
            const module = getModuleById(id);
            if (!module) return null;
            const Icon = module.icon;
            return (
              <Button
                key={id}
                variant="outline"
                className="h-auto py-2 sm:py-3 px-2 sm:px-3 flex flex-col items-center gap-1 sm:gap-1.5 hover:bg-primary/10 hover:border-primary/50 transition-all active:scale-95"
                onClick={() => navigate(module.href)}
              >
                <Icon className="w-4 h-4 sm:w-5 sm:h-5 text-primary" />
                <span className="text-[10px] sm:text-xs font-medium text-center leading-tight line-clamp-2">{module.name}</span>
              </Button>
            );
          })}
          {quickAccess.length < 4 && (
            <Button
              variant="outline"
              className="h-auto py-2 sm:py-3 px-2 sm:px-3 flex flex-col items-center gap-1 sm:gap-1.5 border-dashed hover:bg-muted/50 active:scale-95"
              onClick={() => setIsConfigOpen(true)}
            >
              <Plus className="w-4 h-4 sm:w-5 sm:h-5 text-muted-foreground" />
              <span className="text-[10px] sm:text-xs text-muted-foreground">Añadir</span>
            </Button>
          )}
        </div>
      </CardContent>
    </Card>
  );
}
